import React, { useRef, useEffect, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR, { mutate as swrMutate } from 'swr';
import { Loader2, PlusCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCurrentZone } from '../hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useT } from '@/contexts/I18nContext';
import { listCameras, updateCamera, type CameraDto } from '../api/cameras';
import { listProviders, type CameraProviderDto } from '../api/providers';
import { cameraTypeLabel } from './equipment/helpers';
import { UnifiedCameraDialog } from './equipment/UnifiedCameraDialog';
import LocalComputerPanel from './equipment/LocalComputerPanel';
import RouterPanel from './equipment/RouterPanel';
import NVRPanel from './equipment/NVRPanel';
import MicrophonePanel, { micSwrKey } from './equipment/MicrophonePanel';
import AddDeviceDropdown from './equipment/AddDeviceDropdown';

const EquipmentSettings: React.FC = () => {
  const { t } = useT();
  const { zoneId } = useCurrentZone();

  const camerasSWRKey = zoneId ? `equipment:cameras:${zoneId}` : null;
  const providersSWRKey = zoneId ? `equipment:providers:${zoneId}` : null;

  const { data: cameras, isLoading: loadingCameras, error: camerasError, mutate: mutateCameras } =
    useSWR<CameraDto[]>(camerasSWRKey, () => listCameras(1000, zoneId!).then(r => r.data.results));

  const { data: providers, mutate: mutateProviders } =
    useSWR<CameraProviderDto[]>(providersSWRKey, () => listProviders(zoneId!).then(r => r.data.results), {
      shouldRetryOnError: false,
    });

  const providerMap = new Map((providers ?? []).map(p => [String(p.camera), p]));

  const onlineRecheckTimeouts = useRef<number[]>([]);
  useEffect(() => () => { onlineRecheckTimeouts.current.forEach(clearTimeout); }, []);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState<CameraDto | null>(null);

  function openAdd() { setEditingCamera(null); setDialogOpen(true); }
  function openEdit(cam: CameraDto) { setEditingCamera(cam); setDialogOpen(true); }

  function handleSaved() {
    void mutateCameras();
    void mutateProviders();
    onlineRecheckTimeouts.current = [2000, 4000, 6000].map(ms =>
      window.setTimeout(() => { void mutateCameras(); }, ms),
    );
  }

  async function handleToggleActive(e: React.SyntheticEvent, cam: CameraDto) {
    e.stopPropagation();
    try {
      await updateCamera(cam.id, { active: !cam.active });
      void mutateCameras();
    } catch { /* ignore */ }
  }

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  const handleDeviceCreated = () => {
    // Cover every panel/section that could surface the new device:
    // router (RouterPanel's SWR), cameras + providers (table), and any
    // future per-zone NVR listing keyed off the zone id.
    void swrMutate(`zone-router:${zoneId}`);
    void swrMutate(`zone-nvrs:${zoneId}`);
    void swrMutate(micSwrKey(zoneId));
    void mutateCameras();
    void mutateProviders();
  };

  return (
    <div className="space-y-4">
      <LocalComputerPanel
        zoneId={zoneId}
        onImported={() => {
          // LC peer discovery (Group D) can plant any of camera / NVR
          // / router; the sibling RouterPanel / NVRPanel SWR keys are
          // mutated inside LocalComputerPanel — but the camera table
          // below this card still needs a kick to reflect a new row.
          void mutateCameras();
          void mutateProviders();
        }}
      />
      <RouterPanel
        zoneId={zoneId}
        onCamerasImported={() => {
          // Router autodiscovery just minted Camera + provider rows;
          // refresh the table below so they appear immediately rather
          // than waiting for the next focus-revalidate.
          void mutateCameras();
          void mutateProviders();
        }}
      />
      <NVRPanel zoneId={zoneId} />
      <MicrophonePanel zoneId={zoneId} />

      <Card>
      <CardContent className="space-y-4">
        <h2 className="mb-3 text-base font-semibold">Камеры</h2>

        {loadingCameras && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Загрузка...
          </div>
        )}

        {camerasError && !loadingCameras && !cameras && (
          <ServerStatusNotice variant="card" error={camerasError} onRetry={() => mutateCameras()} />
        )}

        {!loadingCameras && (!camerasError || !!cameras) && (
          <>
            {!cameras || cameras.length === 0 ? (
              <p className="text-sm text-muted-foreground">В этом филиале пока нет камер.</p>
            ) : (
              <div className="overflow-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Название', 'Тип', 'Подключение', 'Статус', 'Активна'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cameras.map(cam => {
                      const prov = providerMap.get(String(cam.id));
                      return (
                        <tr
                          key={cam.id}
                          onClick={() => openEdit(cam)}
                          className="cursor-pointer border-t border-border transition-colors hover:bg-muted/30"
                        >
                          <td className="px-3 py-2 font-medium">
                            {cam.name || cam.trassir_name}
                          </td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            {cameraTypeLabel(cam.type ?? '')}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                            {prov ? (
                              <>
                                {prov.ip_address}
                                {prov.port ? `:${prov.port}` : ''}
                                {prov.channel ? <span className="text-muted-foreground/60"> · ch{prov.channel}</span> : null}
                              </>
                            ) : (
                              <span className="text-muted-foreground/50">Не настроено</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn(
                              'rounded-full border px-2 py-0.5 text-xs font-medium',
                              cam.online
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
                                : 'border-border bg-muted text-muted-foreground',
                            )}>
                              {cam.online ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={cam.active}
                              onChange={e => handleToggleActive(e, cam)}
                              className="cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* "Добавить камеру" duplicate dropped 2026-05-26 — the
                canonical add affordance is the AddDeviceDropdown at
                the bottom of the page, which covers router/NVR/camera
                in one place. */}
          </>
        )}

        <UnifiedCameraDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSaved={handleSaved}
          zoneId={zoneId}
          camera={editingCamera}
          provider={editingCamera ? providerMap.get(String(editingCamera.id)) : undefined}
        />
      </CardContent>
      </Card>

      {/* Add-device dropdown moved to the bottom of the page so the
          existing equipment reads top-down (computer → router → NVR
          → cameras) and the "add another thing" affordance is the
          last visual stop. */}
      <AddDeviceDropdown zoneId={zoneId} onCreated={handleDeviceCreated} />
    </div>
  );
};

export default EquipmentSettings;
